import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';

const TERMS_OPTIONS = [
  { value: 'due_on_receipt', label: 'Due on receipt' },
  { value: 'net_10', label: 'Net 10' },
  { value: 'net_15', label: 'Net 15' },
  { value: 'net_30', label: 'Net 30' },
  { value: 'net_45', label: 'Net 45' },
  { value: 'net_60', label: 'Net 60' },
  { value: 'net_90', label: 'Net 90' },
];

const CUSTOMER_TYPES = [
  { value: '', label: 'None' },
  { value: 'wholesale', label: 'Wholesale' },
  { value: 'retail', label: 'Retail' },
  { value: 'government', label: 'Government' },
  { value: 'distributor', label: 'Distributor' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'oem', label: 'OEM' },
  { value: 'other', label: 'Other' },
];

// ---------------------------------------------------------------------------
// Collapsible Section
// ---------------------------------------------------------------------------
const Section = ({ title, icon, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-zinc-50 hover:bg-zinc-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium text-zinc-700">{title}</span>
        </div>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className={`text-zinc-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Customer Form (slide-out panel)
// ---------------------------------------------------------------------------
const CustomerForm = ({ customer, onSave, onCancel, saving }) => {
  const isEdit = !!customer;
  const [form, setForm] = useState({
    display_name: customer?.display_name || '',
    first_name: customer?.first_name || '',
    last_name: customer?.last_name || '',
    company_name: customer?.company_name || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    website: customer?.website || '',
    address_line1: customer?.address_line1 || '',
    address_line2: customer?.address_line2 || '',
    city: customer?.city || '',
    state: customer?.state || '',
    zip: customer?.zip || '',
    customer_type: customer?.customer_type || '',
    payment_terms: customer?.payment_terms || 'net_30',
    notes: customer?.notes || '',
  });

  // Custom fields as array of {key, value} for easy editing
  const existingCustom = customer?.custom_fields && typeof customer.custom_fields === 'object' ? customer.custom_fields : {};
  const [customFields, setCustomFields] = useState(
    Object.entries(existingCustom).map(([key, value]) => ({ key, value: String(value) }))
  );

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const addCustomField = () => setCustomFields((prev) => [...prev, { key: '', value: '' }]);
  const removeCustomField = (idx) => setCustomFields((prev) => prev.filter((_, i) => i !== idx));
  const updateCustomField = (idx, field, val) => setCustomFields((prev) => prev.map((cf, i) => i === idx ? { ...cf, [field]: val } : cf));

  // Auto-generate display name from first + last if display_name is empty
  useEffect(() => {
    if (!isEdit && !form.display_name && (form.first_name || form.last_name)) {
      const auto = [form.first_name, form.last_name].filter(Boolean).join(' ');
      setForm((prev) => ({ ...prev, display_name: auto }));
    }
  }, [form.first_name, form.last_name, isEdit, form.display_name]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Convert custom fields array back to object
    const cfObj = {};
    for (const cf of customFields) {
      const k = cf.key.trim();
      if (k) cfObj[k] = cf.value;
    }
    onSave({ ...form, custom_fields: cfObj });
  };

  const inputCls = "w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onCancel}>
      <div
        className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-xl animate-[slideIn_200ms_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-zinc-900">{isEdit ? 'Edit Customer' : 'New Customer'}</h2>
            <button onClick={onCancel} className="p-1.5 hover:bg-zinc-100 rounded-md transition-colors">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4l8 8M12 4l-8 8" /></svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* ── Name and Contact ── */}
            <Section
              title="Name and contact"
              icon={<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400"><circle cx="8" cy="5" r="3" /><path d="M2 14c0-3 2.5-5 6-5s6 2 6 5" /></svg>}
            >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">First Name</label>
                  <input type="text" value={form.first_name} onChange={set('first_name')} placeholder="John" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Last Name</label>
                  <input type="text" value={form.last_name} onChange={set('last_name')} placeholder="Smith" className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Company Name</label>
                  <input type="text" value={form.company_name} onChange={set('company_name')} placeholder="Acme Corp" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Display Name *</label>
                  <input type="text" value={form.display_name} onChange={set('display_name')} required placeholder="How they appear on invoices" className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Email</label>
                  <input type="email" value={form.email} onChange={set('email')} placeholder="billing@company.com" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Phone</label>
                  <input type="tel" value={form.phone} onChange={set('phone')} placeholder="(555) 555-0100" className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Website</label>
                <input type="url" value={form.website} onChange={set('website')} placeholder="https://company.com" className={inputCls} />
              </div>
            </Section>

            {/* ── Addresses ── */}
            <Section
              title="Addresses"
              icon={<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400"><path d="M8 1C5.2 1 3 3.2 3 6c0 4 5 9 5 9s5-5 5-9c0-2.8-2.2-5-5-5Z" /><circle cx="8" cy="6" r="1.5" /></svg>}
            >
              <p className="text-xs font-medium text-zinc-600 mb-1">Billing Address</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Street Address 1</label>
                  <input type="text" value={form.address_line1} onChange={set('address_line1')} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Street Address 2</label>
                  <input type="text" value={form.address_line2} onChange={set('address_line2')} placeholder="Suite, unit, etc." className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">City</label>
                  <input type="text" value={form.city} onChange={set('city')} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">State</label>
                  <input type="text" value={form.state} onChange={set('state')} maxLength={2} placeholder="OH" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">ZIP</label>
                  <input type="text" value={form.zip} onChange={set('zip')} className={inputCls} />
                </div>
              </div>

            </Section>

            {/* ── Payments ── */}
            <Section
              title="Payments"
              icon={<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400"><rect x="1" y="4" width="14" height="9" rx="1.5" /><path d="M1 7h14" /></svg>}
              defaultOpen={false}
            >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Default Payment Terms</label>
                  <select value={form.payment_terms} onChange={set('payment_terms')} className={`${inputCls} bg-white`}>
                    {TERMS_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Customer Type</label>
                  <select value={form.customer_type} onChange={set('customer_type')} className={`${inputCls} bg-white`}>
                    {CUSTOMER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
            </Section>

            {/* ── Additional Info ── */}
            <Section
              title="Additional info"
              icon={<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400"><path d="M9 1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5L9 1Z" /><path d="M9 1v4h4" /></svg>}
              defaultOpen={false}
            >
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Notes</label>
                <textarea value={form.notes} onChange={set('notes')} rows={3} placeholder="Internal notes about this customer" className={`${inputCls} resize-none`} />
              </div>
            </Section>

            {/* ── Custom Fields ── */}
            <Section
              title="Custom fields"
              icon={<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400"><path d="M12 2H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1Z" /><path d="M6 5h4M6 8h4M6 11h2" /></svg>}
              defaultOpen={customFields.length > 0}
            >
              <p className="text-xs text-zinc-400 mb-3">Add custom details to track anything specific to this customer.</p>
              {customFields.length > 0 && (
                <div className="space-y-2">
                  {customFields.map((cf, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={cf.key}
                        onChange={(e) => updateCustomField(idx, 'key', e.target.value)}
                        placeholder="Field name"
                        className="flex-1 px-3 py-1.5 border border-zinc-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900"
                      />
                      <input
                        type="text"
                        value={cf.value}
                        onChange={(e) => updateCustomField(idx, 'value', e.target.value)}
                        placeholder="Value"
                        className="flex-1 px-3 py-1.5 border border-zinc-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900"
                      />
                      <button
                        type="button"
                        onClick={() => removeCustomField(idx)}
                        className="p-1.5 text-zinc-300 hover:text-red-500 transition-colors shrink-0"
                        title="Remove field"
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4l8 8M12 4l-8 8" /></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={addCustomField}
                className="mt-2 text-sm text-zinc-500 hover:text-zinc-900 transition-colors flex items-center gap-1"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 3v10M3 8h10" /></svg>
                Add custom field
              </button>
            </Section>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t border-zinc-100">
              <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Cancel</button>
              <button type="submit" disabled={saving || !form.display_name.trim()} className="px-5 py-2 bg-zinc-900 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {saving ? 'Saving...' : isEdit ? 'Update Customer' : 'Create Customer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main CustomersView
// ---------------------------------------------------------------------------
const CustomersView = ({ loaded, onNavigateToInvoicing }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await apiFetch('/api/customers');
      const data = await res.json();
      setCustomers(data.customers || []);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const filtered = useMemo(() => {
    if (!search.trim()) return customers;
    const q = search.toLowerCase();
    return customers.filter((c) =>
      c.display_name?.toLowerCase().includes(q) ||
      c.company_name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.city?.toLowerCase().includes(q) ||
      c.customer_type?.toLowerCase().includes(q)
    );
  }, [customers, search]);

  const handleSave = async (fields) => {
    setSaving(true);
    try {
      if (editingCustomer) {
        const res = await apiFetch(`/api/customers/${editingCustomer.id}`, { method: 'PUT', body: JSON.stringify(fields) });
        if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
        setMsg({ type: 'success', text: 'Customer updated' });
      } else {
        const res = await apiFetch('/api/customers', { method: 'POST', body: JSON.stringify(fields) });
        if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
        setMsg({ type: 'success', text: 'Customer created' });
      }
      setShowForm(false);
      setEditingCustomer(null);
      fetchCustomers();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Remove ${customer.display_name}? This will deactivate the customer.`)) return;
    try {
      const res = await apiFetch(`/api/customers/${customer.id}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setMsg({ type: 'success', text: 'Customer removed' });
      fetchCustomers();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  const openEdit = (customer) => {
    setEditingCustomer(customer);
    setShowForm(true);
  };

  const openCreate = () => {
    setEditingCustomer(null);
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div
        className="flex items-center justify-between mb-6 transition-all duration-700"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '200ms' }}
      >
        <div>
          <h1 className="text-2xl font-light text-zinc-900">Customers</h1>
          <p className="text-sm text-zinc-400">{customers.length} active customer{customers.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={openCreate} className="px-4 py-2 bg-zinc-900 text-white text-sm rounded-md hover:bg-zinc-800 transition-colors flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3v10M3 8h10" /></svg>
          Add Customer
        </button>
      </div>

      {/* Messages */}
      {msg && (
        <div className={`mb-4 px-4 py-2 rounded-md text-sm ${msg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
          {msg.text}
          <button onClick={() => setMsg(null)} className="ml-2 underline text-xs">dismiss</button>
        </div>
      )}

      {/* Empty state */}
      {customers.length === 0 ? (
        <div className="text-center py-16" style={{ opacity: loaded ? 1 : 0, transitionDelay: '400ms' }}>
          <div className="w-16 h-16 rounded-2xl bg-zinc-100 flex items-center justify-center mx-auto mb-4">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="14" cy="10" r="5" />
              <path d="M5 24c0-5 4-8 9-8s9 3 9 8" />
            </svg>
          </div>
          <h2 className="text-lg font-medium text-zinc-900 mb-2">No customers yet</h2>
          <p className="text-sm text-zinc-400 mb-6">Add your first customer to start creating invoices.</p>
          <button onClick={openCreate} className="px-5 py-2.5 bg-zinc-900 text-white text-sm rounded-md hover:bg-zinc-800 transition-colors">
            Add Customer
          </button>
        </div>
      ) : (
        <>
          {/* Search */}
          <div className="mb-4">
            <div className="relative w-64">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="7" cy="7" r="5" /><path d="M11 11L14 14" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search customers..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-1 focus:ring-zinc-900"
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
            <div className="grid grid-cols-[1fr_100px_140px_160px_100px_80px] gap-0 bg-zinc-50 border-b border-zinc-200 px-4 py-2.5 text-xs font-medium text-zinc-500 uppercase tracking-wider">
              <div>Customer</div>
              <div>Type</div>
              <div>Location</div>
              <div>Email</div>
              <div>Terms</div>
              <div />
            </div>
            <div className="divide-y divide-zinc-100">
              {filtered.map((c) => (
                <div key={c.id} className="grid grid-cols-[1fr_100px_140px_160px_100px_80px] gap-0 px-4 py-3 items-center hover:bg-zinc-50 transition-colors">
                  <div>
                    <p className="text-sm font-medium text-zinc-900">{c.display_name}</p>
                    {c.company_name && c.company_name !== c.display_name && (
                      <p className="text-xs text-zinc-400">{c.company_name}</p>
                    )}
                  </div>
                  <div className="text-xs text-zinc-400 capitalize">{c.customer_type || '—'}</div>
                  <div className="text-sm text-zinc-500 truncate">
                    {[c.city, c.state].filter(Boolean).join(', ') || '—'}
                  </div>
                  <div className="text-sm text-zinc-500 truncate">{c.email || '—'}</div>
                  <div className="text-xs text-zinc-400">
                    {TERMS_OPTIONS.find((t) => t.value === c.payment_terms)?.label || c.payment_terms}
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(c)} className="p-1.5 text-zinc-400 hover:text-zinc-700 transition-colors rounded" title="Edit">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M11 2l3 3L5 14H2v-3L11 2Z" /></svg>
                    </button>
                    <button onClick={() => handleDelete(c)} className="p-1.5 text-zinc-300 hover:text-red-500 transition-colors rounded" title="Remove">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 5h10M5.5 5V3.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1V5M6.5 7.5v4M9.5 7.5v4" /><path d="M4 5l.5 8a1.5 1.5 0 0 0 1.5 1.5h4a1.5 1.5 0 0 0 1.5-1.5L12 5" /></svg>
                    </button>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-zinc-400">No customers match your search</div>
              )}
            </div>
          </div>
          <div className="mt-3 text-xs text-zinc-400 text-right">{filtered.length} of {customers.length} customers</div>
        </>
      )}

      {/* Form slide-out */}
      {showForm && (
        <CustomerForm
          customer={editingCustomer}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingCustomer(null); }}
          saving={saving}
        />
      )}
    </div>
  );
};

export default CustomersView;
